import {useState} from 'react';
import {Button, Progress} from '@tinct/web-components';
import {GameModal} from '@tinct/web-layout';
import {Ghost} from 'lucide-react';
import {pluralize} from '@tinct/core-utils';

type GeneratingOverlayProps = {
  remainingCount: number;
  completedCount: number;
  totalCount: number;
  estimatedTimeLeftInSeconds?: number | null;
  onLetRunInBackground?: () => void;
  onCancel?: () => void;
};

function formatTimeLeft(seconds: number): string {
  if (seconds < 60) return 'less than a minute';
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes > 1 ? 's' : ''}`;
}

export const GeneratingOverlay = ({
  remainingCount,
  completedCount,
  totalCount,
  estimatedTimeLeftInSeconds,
  onLetRunInBackground,
  onCancel,
}: GeneratingOverlayProps) => {
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const [gameOpen, setGameOpen] = useState(false);

  return (
    <>
    {/* z-40: above everything in the canvas (toolbar z-20), below the pages
        side panel (z-50) and the body-portaled dialogs like the game modal. */}
    <div className="absolute inset-0 z-40 flex items-center justify-center backdrop-blur-[32px]">
      <div className="absolute inset-0 bg-muted-foreground/70" />

      <div className="relative flex w-[768px] max-w-[90%] flex-col gap-6">
        <div>
          <h2 className="font-heading text-3xl font-semibold tracking-[-0.5px] text-background">
            We&apos;re on it!
          </h2>
          <h2 className="font-heading text-3xl font-semibold tracking-[-0.5px] text-background">
            Generating the {remainingCount} remaining {pluralize(remainingCount, 'page')}...
          </h2>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-background">
            {estimatedTimeLeftInSeconds != null
              ? `Estimated time: ${formatTimeLeft(estimatedTimeLeftInSeconds)}`
              : `${completedCount} of ${totalCount} pages generated`}
          </p>
          <Progress
            value={progress}
            className="h-2 bg-foreground/90 [&>[data-slot=progress-indicator]]:bg-[linear-gradient(90deg,rgb(249,168,212),rgb(99,102,241),rgb(103,232,249))]"
          />
        </div>

        <p className="text-sm text-background">
          You can stay (and play?) or let it run in the background.
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {onLetRunInBackground && (
              <Button onClick={onLetRunInBackground}>
                Let it run in background
              </Button>
            )}
            {onCancel && (
              <Button variant="destructive" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-background hover:bg-background/30 hover:text-background"
            onClick={() => setGameOpen(true)}
          >
            <Ghost className="size-4" />
          </Button>
        </div>
      </div>
    </div>
    <GameModal open={gameOpen} onOpenChange={setGameOpen} />
    </>
  );
};
